// Phrase autotranslates en.json into the other locales, and the machine
// translator sometimes translates the placeholder NAME along with the sentence:
// "Filter by {{item}}" becomes "Suodata {{kohdan}} mukaan" in Finnish. i18next
// then finds no value for {{kohdan}} and leaves the raw token on screen, which
// also collapses every option of a dropdown into the same string.
//
// This resolves such a renamed placeholder back to the value the caller passed,
// so a translation bug degrades to "wrong-looking token" only when we genuinely
// cannot tell which value was meant.

// t() options that are i18next's own, never interpolation values. `count` and
// `context` are deliberately absent: both are used as real values in en.json.
const I18NEXT_OPTIONS = [
  "lng",
  "lngs",
  "ns",
  "fallbackLng",
  "keySeparator",
  "nsSeparator",
  "replace",
  "interpolation",
  "defaultVariables",
  "postProcess",
  "returnObjects",
  "returnDetails",
  "joinArrays",
  "skipInterpolation",
  "ordinal",
];

const isValueOption = ([key, value]) =>
  !I18NEXT_OPTIONS.includes(key) &&
  !key.startsWith("defaultValue") &&
  (typeof value === "string" || typeof value === "number");

export const resolveTranslatedPlaceholder = (text, match, options) => {
  const fallback = match[0];
  if (!options || typeof options !== "object") return fallback;

  const data =
    options.replace && typeof options.replace === "object"
      ? options.replace
      : options;

  // A malformed placeholder such as {{{defaultLabel}} matches with the brace
  // still attached, so strip braces before looking the name up.
  const name = String(match[1] ?? "").replace(/[{}]/g, "").trim();
  if (!name) return fallback;
  if (name in data) return String(data[name]);

  const values = Object.entries(data).filter(isValueOption);
  if (!values.length) return fallback;

  // Positional placeholders keep their suffix through translation: en's
  // {{itemY}} becomes fi's {{eräY}}, so the trailing capital identifies it.
  const suffix = name.match(/([A-Z])$/)?.[1];
  if (suffix) {
    const positional = values.find(([key]) => key.endsWith(suffix));
    if (positional) return String(positional[1]);
  }

  // Otherwise take a value the string has not used yet — neither still waiting
  // as {{key}} nor already substituted earlier in this same pass.
  const unused = values.filter(
    ([key, value]) =>
      !text.includes(`{{${key}}}`) && !text.includes(String(value))
  );
  if (unused.length) return String(unused[0][1]);

  return fallback;
};
